<template>
  <div class="form-container">
    <form @submit.prevent="sendEmail" @keydown.enter.prevent>
      <div class="container mx-auto p-6 bg-white shadow-lg rounded-lg w-[700px]">
        <h2>{{ building.building_nm }} - 請求書メール作成</h2>

        <div class="mb-2">
          <EmailInput label="宛先 (TO)" v-model="sendEmailInfo.to" :filteredEmails="filteredToEmails" />
        </div>

        <div class="mb-2">
          <EmailInput label="宛先 (CC)" v-model="sendEmailInfo.cc" :filteredEmails="filteredCcEmails" />
        </div>

        <div class="mb-2">
          <EmailInput label="宛先 (BCC)" v-model="sendEmailInfo.bcc" :filteredEmails="filteredBccEmails" />
        </div>

        <div class="mb-3">
          <label>件名</label>
          <input v-model="sendEmailInfo.subject" class="form-control" />
        </div>

        <div class="mb-3">
          <label>本文</label>
          <textarea v-model="sendEmailInfo.body" class="form-control" rows="15"></textarea>
        </div>

        <div class="mb-2">
          <label>添付ファイル</label>
          <div v-if="sendEmailInfo.file">
            <!-- ✅ <p> を <a> に変更し、クリックでダウンロード -->
            <a :href="getPdfUrl()" :download="sendEmailInfo.file.name" class="file-link">
              {{ sendEmailInfo.file.name }}
            </a>
          </div>
          <div v-else>
            <p>添付ファイルなし</p>
          </div>
        </div>

        <div class="text-end">
          <router-link :to="{
              name: 'BuildingWorkMonthlyBillingDetail',
              params: { building_id: buildingId, year: year, month: month },
              query: { year: year, month: month }
            }"
          >
              <button type="button" class="btn btn-secondary mx-1">キャンセル</button>
          </router-link>
          <button class="btn btn-success ms-2" type="button" @click="sendEmail">送信</button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, computed, onMounted, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import { getBuilding, getBuildingMonthlyWorkBillingDetails, sendBuildingMonthlyWorkBilling } from '@/api/axios';
import EmailInput from "@/components/shared/EmailInput.vue"; // 📌 ここで EmailInput をインポート

export default {
  components: { EmailInput }, // 📌 ここで登録
  setup() {
    const title = ref('年間作業予定請求書メール送信画面');

    watchEffect(() => {
      document.title = title.value;
    });

    const userId = ref('');
    const userName = ref('');
    const building = ref({});
    const billing = ref({});
    const customerRepresentatives = ref([]);
    const file = ref(null);

    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const buildingId = route.params.building_id;
    const year = route.query.year || new Date().getFullYear();
    const month = route.query.month || new Date().getMonth() + 1;

    const sendEmailInfo = ref({
      to: [],
      cc: [],
      bcc: [],
      subject: '請求書送付のお知らせ',
      body: '',
      file: '',
      // 他のフィールド
    });


    const fetchWorkScheduleDatas = async () => {
      userId.value = localStorage.getItem('userId');
      userName.value = localStorage.getItem('userName');
      
      try {
        const response = await getBuilding(buildingId);
        building.value = response.data;
      } catch (error) {
        console.error('建物情報データの取得に失敗しました:', error);
      }
      
      try {
        const response = await getBuildingMonthlyWorkBillingDetails(buildingId, year, month);
        billing.value = response.data.annual_work_monthly_billing;

        const customer_rep = billing.value.m_customer_representative;
        if (customer_rep) {
          customerRepresentatives.value = Array.isArray(customer_rep) ? customer_rep : [customer_rep];
        } else {
          customerRepresentatives.value = [];
        }

        // `sendEmailInfo.to` にメールアドレスのみを格納
        sendEmailInfo.value.to = customerRepresentatives.value
          .map(rep => rep.in_charge_email)
          .filter(email => email);

        const customer_nm = building.value.m_customer.customer_nm;
        const building_nm = building.value.building_nm;
        sendEmailInfo.value.subject = `【送付】${billing.value.plan_year}年${billing.value.plan_month}月度 ${customer_nm} ${building_nm}請求書`;

        let company = '';
        let in_charge_dep = '';
        let in_charge_nm = '';
        let first_body = ''

        if(customer_rep){
          company = billing.value.m_customer?.customer_nm;
          in_charge_dep = customer_rep.in_charge_department_nm;
          in_charge_nm = customer_rep.in_charge_last_nm;
           
          if(in_charge_dep && in_charge_nm){
            first_body = `${company}\n${in_charge_dep} ${in_charge_nm}様`;
          }else if (!(in_charge_dep) && in_charge_nm){
            first_body = `${company}\n${in_charge_nm}様`;
          }else if (in_charge_dep && !(in_charge_nm)){
             first_body = `${company}\n${in_charge_dep} ご担当者様`;
          }else{
            first_body = `${company}\nご担当者様`;
          }
        }else{
          first_body = 'ご担当者様';
        }
        sendEmailInfo.value.body = `${first_body}\n\nいつもお世話になっております。\n今月の請求書を送付させていただきます。\n御査収ください。\n\n次回もどうぞ宜しくお願い致します。\n\n株式会社 創英企画\n${userName.value}\nTEL: 03-3370-8006\nFAX: 3370-8157\nMail: info@soei-planning.com`;
      } catch (error) {
        console.error('年間スケジュールの取得に失敗しました:', error);
      }
    };

    // 宛先変更時に配列を保持
    // customer_representatives から email だけを抽出
    const filteredToEmails = computed(() =>
      customerRepresentatives.value.map(rep => rep.in_charge_email).filter(email => email)
    );
    const filteredCcEmails = computed(() =>
      customerRepresentatives.value.map(rep => rep.in_charge_email).filter(email => email)
    );
    const filteredBccEmails = computed(() =>
      customerRepresentatives.value.map(rep => rep.in_charge_email).filter(email => email)
    );

    console.log('あああああああああああ')

    onMounted(async () => {
      await fetchWorkScheduleDatas();  // ✅ `await` でデータ取得を待つ

      let pdfData = sessionStorage.getItem("pdfData");

      if (pdfData) {
        try {
          // Base64 → バイナリ変換
          const byteCharacters = atob(pdfData);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const pdfBlob = new Blob([byteArray], { type: "application/pdf" });

          // Blob → File に変換
          sendEmailInfo.value.file = new File([pdfBlob], `${building.value.building_nm}_${month}月度_請求書.pdf`, { type: "application/pdf" });

        } catch (error) {
          console.error("PDF 変換エラー:", error);
        }
      } else {
        console.log("pdfData is missing!");
      }
    });

    const getPdfUrl = () => {
      if (!file.value) return "#";
      return URL.createObjectURL(file.value);
    };

    // Railsへメール送信リクエスト
    const sendEmail = async () => {
      const formData = new FormData();
      // 配列として追加する
      sendEmailInfo.value.to.forEach(email => formData.append("to[]", email));
      sendEmailInfo.value.cc.forEach(email => formData.append("cc[]", email));
      sendEmailInfo.value.bcc.forEach(email => formData.append("bcc[]", email));
      formData.append("subject", sendEmailInfo.value.subject);
      formData.append("body", sendEmailInfo.value.body);

      if (sendEmailInfo.value.file) {
        formData.append("file", sendEmailInfo.value.file);
      }

      try {
        await sendBuildingMonthlyWorkBilling(formData);  // ✅ そのまま送信
        store.dispatch('setFlashMessage', { message: '年間予定作業請求書メール送信完了！', type: 'success' });
        router.push({ path: `/building-work-monthly-billing-detail/${buildingId}/${year}/${month}`, query: { year, month } });
      } catch (error) {
        console.error('送信中にエラーが発生しました:', error);
      }
    };

    return {
      title,
      building,
      buildingId,
      year,
      month,
      billing,
      sendEmailInfo,
      customerRepresentatives,
      filteredToEmails,
      filteredCcEmails,
      filteredBccEmails,
      file,
      getPdfUrl,
      sendEmail
    };
  }
};
</script>

<style scoped>
.container {
  max-width: 1000px;
  margin: auto;
  background-color: #f9f9f9;
  padding: 16px 24px;
  border-radius: 10px;
  box-shadow: 2px 2px 2px rgba(0, 0, 0, 0.1);
}

label {
  font-weight: bold;
}

label{
  display: inline-block;
  width: 120px;
}

input, textarea {
  border: 1px solid #ccc;
  padding: 8px;
  font-size: 14px;
}

button {
  transition: background-color 0.2s;
}

button:hover {
  opacity: 0.8;
}

.email-input-container {
  display: flex;
  flex-wrap: wrap;
  border: 1px solid #ccc;
  padding: 5px;
  min-height: 32px;
  align-items: center;
}

.email-tag {
  background-color: #e0e0e0;
  padding: 5px 10px;
  margin: 2px;
  border-radius: 15px;
  display: flex;
  align-items: center;
}

.remove {
  margin-left: 5px;
  cursor: pointer;
}

.email-input {
  border: none;
  outline: none;
  flex: 1;
  padding: 5px;
}
</style>
